import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';

function ChartTooltipContent({ active, payload, label, t }) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">
              {payload[0].payload.period === 'day' ? t('time') : t('date')}
            </span>
            <span className="font-bold text-muted-foreground">{label}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">Rate</span>
            <span className="font-bold">{payload[0].value.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export function RecyclingChart({ data }) {
  const { t, i18n } = useTranslation();

  const chartData = data?.historical_data?.map(item => ({
    date: item.date,
    recyclingRate: item.rate,
    period: data.period
  })) || [];

  const currentRate = data?.recycling_rate || 0;
  const percentage  = currentRate.toFixed(1);
  const period      = data?.period || 'year';

  const getColorScheme = (rate) => {
    if (rate >= 70) return { stroke: '#10b981', fill: '#10b981', fillOpacity: 0.2, text: 'text-green-600' };
    if (rate >= 50) return { stroke: '#3b82f6', fill: '#3b82f6', fillOpacity: 0.2, text: 'text-blue-500' };
    return               { stroke: '#ef4444', fill: '#ef4444', fillOpacity: 0.2, text: 'text-red-500' };
  };

  const colorScheme = getColorScheme(currentRate);

  const getRangeLabel = () => {
    if (!data?.start_date || !data?.end_date) return null;
    const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
    const isCustom = period?.startsWith('custom');
    if (isCustom)        return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
    if (period === 'day')   return fmt(data.start_date);
    if (period === 'month') return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
    if (period === 'year')  return new Date(data.start_date).getFullYear().toString();
    return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium pb-2">{t('recyclingRate.defaultTitle')}</h2>
        {getRangeLabel() && (
          <span className="text-xs text-muted-foreground pb-2">{getRangeLabel()}</span>
        )}
      </div>

      <div className={`px-4 pb-1 text-2xl font-bold ${colorScheme.text}`}>
        {percentage}%
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval="preserveStartEnd"
              tickFormatter={(value) => {
                if (period === 'day') return new Date(value).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
                if (period === 'week') return value;
                // Custom periods return pre-formatted strings ("07 Feb", "Feb 2026") — pass through as-is
                if (period?.startsWith('custom')) return value;
                try {
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return value;
                  return date.toLocaleDateString(i18n.language, { month: 'short' });
                } catch (e) { return value; }
              }}
            />
            <YAxis hide domain={[0, 100]} />
            <Tooltip content={<ChartTooltipContent t={t} />} wrapperStyle={{ outline: 'none' }} />
            <Area
              type="monotone"
              dataKey="recyclingRate"
              stroke={colorScheme.stroke}
              strokeWidth={3}
              fill={colorScheme.fill}
              fillOpacity={colorScheme.fillOpacity}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}